import React, {
  createContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useContext
} from 'react';
import PropTypes from 'prop-types';
import { ApplicationContext } from 'shop/context/ApplicationContext';
import { useQuickView } from '../QuickView/QuickView.context';
import { updateURL, getDefaults } from './Product.utils';
import { generateChildMap } from './Product.utils.deprecated';
import * as MapUtils from '../../util/MapUtils';
import {
  colorPropType,
  variantPropType,
  sizesPropType
} from './Product.propTypes';

/**
 * @typedef {Object} ProductContext
 * @property {object} currentVariant - Current product variant object
 * @property {map} allVariants - Nested map of product variants
 * @property {map} selectedOptions - Map of selected product options (color, size(s))
 * @property {func} setOption - Updates a key/value pair in selectedOptions
 */
export const ProductContext = createContext();

/**
 * Custom hook for adding derived product state to a component
 * @returns {ProductContext} Product state
 */
export const useProductState = () => {
  const context = React.useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProductState must be used within a ProductProvider');
  }
  return context;
};

/**
 * Custom hook for executing a function only on the components initial render
 * @param {func} fn Function to run on first render
 */
export const useFirstRenderOnly = (fn) => {
  const [hasRendered, setHasRendered] = useState(false);
  useEffect(() => setHasRendered(true), [hasRendered]);
  if (!hasRendered) fn();
};

/**
 * `ProductProvider` is used to provide derived Product context to a component tree.
 */
const ProductProvider = ({
  colors,
  sizes: productSizes,
  demographics,
  variants,
  children
}) => {
  const { queryParams } = useContext(ApplicationContext);
  const {
    isQuickView,
    PDPLink,
    setVariant,
    variant: preservedVariant
  } = useQuickView();
  const urlParams = new URLSearchParams(queryParams);
  // Get search parameters from current url instead of from the server since QuickView is on a PCP
  // This will fetch the current search params on every update to prevent the product from falling back to its default values on every change
  const quickViewParams =
    isQuickView && new URLSearchParams(window.location.search);

  let initialColor = isQuickView
    ? quickViewParams.get('variant')
    : urlParams.get('variant');

  // Support any amount of sizes in a URL (e.g. Size, Kids Size, Adult Size);
  let initialSizes = Array.from(urlParams.entries())
    .filter(([key]) => key.includes('size'))
    .map(([key, value]) => ({ groupName: key, size: value }));

  // Backwards compatibility for supporting Shopify variant IDs
  // IE: /products/mens-ankle-socks?variant=47901464232290
  // The number in the variant parameter encompasses a specific color + size(s) combination
  useFirstRenderOnly(() => {
    if (initialColor && !Number.isNaN(Number(initialColor))) {
      const actualVariant = variants.find(
        (variant) => variant.externalSystems.shopify === initialColor
      );

      if (actualVariant) {
        initialColor = actualVariant.color.id;
        initialSizes = actualVariant.sizes
          .filter((s) => s.title.includes('size'))
          .map(({ title, id }) => ({
            groupName: title,
            size: id
          }));
      } else {
        initialColor = null;
        initialSizes = [];
      }
    }
  });

  const variantsMap = useMemo(
    () =>
      variants.reduce((finalMap, variant) => {
        const currentColor = variant.color?.id;
        const existingMap = finalMap.get(currentColor) || new Map();
        return finalMap.set(
          currentColor,
          generateChildMap(variant, variant.sizes, existingMap)
        );
      }, new Map()),
    [variants]
  );

  const preservedSizeArray =
    isQuickView && preservedVariant.size > 0
      ? Array.from(preservedVariant.entries())
          .filter(([key]) => key.toLowerCase().includes('size'))
          .map(([key, value]) => ({ groupName: key, size: value }))
      : [];

  const validPreservedSizes = preservedSizeArray.filter(
    ({ groupName, size }) => {
      const sizeGroup = productSizes.find(
        (s) => s.id.toLowerCase() === groupName.toLowerCase()
      );
      return sizeGroup && sizeGroup.options.some((opt) => opt.id === size);
    }
  );

  const [selectedOptions, setSelection] = useState(
    getDefaults({
      colors,
      sizes: productSizes,
      selectedColor: initialColor,
      selectedSizes:
        validPreservedSizes.length > 0 ? validPreservedSizes : initialSizes,
      demographics,
      variantsMap
    })
  );

  const setOption = useCallback(
    (key, value) => setSelection(new Map(selectedOptions.set(key, value))),
    []
  );

  /**
   * In QuickView, navigating to a related product in the slider doesn't trigger a full page load.
   * This useEffect updates default values to reflect the newly selected product, ensuring the UI stays in sync.
   */
  useEffect(() => {
    // Ensures QuickView opens with the respective variant from the selected swatch on PCPs
    if (isQuickView) {
      const pdpLinkParams = new URL(PDPLink).searchParams;
      initialColor = pdpLinkParams.get('variant');

      const sizeKeyLookup = new Map(
        productSizes.map((s) => [s.id.toLowerCase(), s.id])
      );

      const sizesFromParams = Array.from(pdpLinkParams.entries()).reduce(
        (acc, [key, value]) => {
          const lowerKey = key.toLowerCase();
          if (sizeKeyLookup.has(lowerKey)) {
            const canonicalKey = sizeKeyLookup.get(lowerKey);
            acc.push({ groupName: canonicalKey, size: value });
          }
          return acc;
        },
        []
      );

      const sizesToUse =
        sizesFromParams.length > 0 ? sizesFromParams : initialSizes;

      setSelection(
        getDefaults({
          colors,
          sizes: productSizes,
          selectedColor: initialColor,
          selectedSizes: sizesToUse,
          demographics,
          variantsMap
        })
      );
    }
  }, [variantsMap]);

  useEffect(() => {
    updateURL(selectedOptions);
    if (isQuickView) {
      setVariant(selectedOptions);
    }
  }, [selectedOptions, variantsMap]);

  const currentVariant = useMemo(() => {
    const variant = MapUtils.deepGet(
      variantsMap,
      Array.from(selectedOptions.values()),
      // Mock current variant when it doesnt exist.
      {
        price: null,
        available: false
      }
    );

    return {
      ...variant,
      images: variant.images?.map((img) => {
        const src = img.image.file.url.includes('http')
          ? img.image.file.url
          : `https:${img.image.file.url}`;

        return {
          id: img.id,
          src,
          alt: img.title
        };
      })
    };
  }, [variantsMap, selectedOptions]);

  return (
    <ProductContext.Provider
      value={{
        currentVariant,
        allVariants: variantsMap,
        selectedOptions,
        setOption
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

ProductProvider.propTypes = {
  /**
   * Swatches objects
   */
  colors: PropTypes.arrayOf(PropTypes.shape(colorPropType)).isRequired,
  /**
   * Content to render inside Product provider tree
   */
  children: PropTypes.node.isRequired,
  /**
   * Product demographics
   */
  demographics: PropTypes.arrayOf(
    PropTypes.shape({ age: PropTypes.string, gender: PropTypes.string })
  ),
  /**
   * Aggregate of product sizes
   */
  sizes: PropTypes.arrayOf(PropTypes.shape(sizesPropType)),
  variants: PropTypes.arrayOf(PropTypes.shape(variantPropType))
};

ProductProvider.defaultProps = {
  demographics: [],
  sizes: [],
  variants: []
};

export default ProductProvider;
